import {
    Box,
    Button,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Spellcheck as SpellcheckIcon,
    CheckBoxOutlineBlank as UnmappedIcon,
    ChevronLeft as MoveLeftIcon,
    ChevronRight as MoveRightIcon,
    CallSplit as SplitIcon,
    Add as AddIcon,
    SwapHoriz as ChangeIcon,
    Close as RemoveIcon
} from '@mui/icons-material';

import React, { useState } from "react";
import WordsChooserComboBox from "./WordsChooserComboBox";

const OneWordEditor = ({ 
    index, precedingText, word, succeedingText, linkTitle,
    translation, moveLeftFunc, moveRightFunc, 
    splitFunc, removeFunc, wordDictionary, 
    onChangeWordDescription, addNewWordFunc,
    dictionaryOpener }) => {

    const [isTextSelected, setIsTextSelected] = useState(false);

    const textSelection = () => {
        const selText = window.getSelection().toString();
        setIsTextSelected(selText.length > 0);
    };

    const splitBtnClicked = () => {
        const selText = window.getSelection().toString();
        const startOffset = word.indexOf(selText);
        const endOffset = startOffset + selText.length;
        splitFunc(index, startOffset, endOffset);
        setIsTextSelected(false);
    };

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5, 
                py: 0.75,
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
                flexWrap: 'wrap'
            }}
        >
            {/* Status icon */}
            {translation !== '' ? (
                <SpellcheckIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
            ) : (
                <UnmappedIcon fontSize="small" color="disabled" sx={{ flexShrink: 0 }} />
            )}

            {/* Boundary controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tooltip title="Add one letter to left">
                    <IconButton size="small" onClick={() => moveLeftFunc(index, -1)}>
                        <MoveLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>|</Typography>
                <Tooltip title="Remove one letter from left">
                    <IconButton size="small" onClick={() => moveLeftFunc(index, 1)}>
                        <MoveRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Preceding text - visible, doesn't break alignment */}
            {precedingText && (
                <Typography 
                    component="span" 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                        maxWidth: 120, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                    }}
                    title={precedingText}
                >
                    {precedingText}
                </Typography>
            )}

            {/* The word being edited */}
            <TextField
                value={word}
                size="small"
                variant="outlined"
                readOnly
                onSelect={textSelection}
                sx={{ 
                    width: 100, 
                    minWidth: 60,
                    '& .MuiInputBase-input': { py: 0.5, px: 1, fontWeight: 500 }
                }}
            />

            {/* Succeeding text */}
            {succeedingText && (
                <Typography 
                    component="span" 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                        maxWidth: 120, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                    }}
                    title={succeedingText}
                >
                    {succeedingText}
                </Typography>
            )}

            {/* Right boundary controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tooltip title="Remove one letter from right">
                    <IconButton size="small" onClick={() => moveRightFunc(index, -1)}>
                        <MoveLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>|</Typography>
                <Tooltip title="Add one letter to right">
                    <IconButton size="small" onClick={() => moveRightFunc(index, 1)}>
                        <MoveRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Split button - when text selected */}
            {isTextSelected && (
                <Tooltip title="Split selected part into new word">
                    <Button size="small" startIcon={<SplitIcon />} onClick={splitBtnClicked}>
                        Split
                    </Button>
                </Tooltip>
            )}

            {/* Translation display - clickable to open dictionary */}
            <TextField
                value={translation}
                size="small"
                variant="outlined"
                readOnly
                onClick={() => dictionaryOpener()}
                placeholder="—"
                sx={{ 
                    width: 220, 
                    minWidth: 160,
                    cursor: 'pointer',
                    '& .MuiInputBase-input': { py: 0.5, px: 1 }
                }}
            />

            {/* Add new word */}
            <Button 
                variant="contained" 
                size="small" 
                startIcon={<AddIcon />}
                onClick={() => addNewWordFunc(index)}
            >
                Add
            </Button>

            {/* Word chooser */}
            <WordsChooserComboBox 
                dictionary={wordDictionary} 
                word={word} 
                wordIndex={index} 
                chosenWordSetter={onChangeWordDescription}
            />

            {/* Grammar link if present */}
            {linkTitle && (
                <TextField
                    value={linkTitle}
                    size="small"
                    variant="outlined"
                    readOnly
                    placeholder="grammar"
                    sx={{ width: 220, minWidth: 160, '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                />
            )}

            {/* Remove word */}
            <Tooltip title="Remove this word">
                <IconButton size="small" color="error" onClick={() => removeFunc(index)}>
                    <RemoveIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const areEqual = (prev, next) => {
    return (
        prev.index === next.index &&
        prev.precedingText === next.precedingText &&
        prev.word === next.word &&
        prev.succeedingText === next.succeedingText &&
        prev.linkTitle === next.linkTitle &&
        prev.translation === next.translation &&
        prev.wordDictionary === next.wordDictionary
    );
};

export default React.memo(OneWordEditor, areEqual);
